import React, { useState, useContext, useEffect } from "react";
import { ReactTransliterate } from "react-transliterate";
import "react-transliterate/dist/index.css";
import { AuthContext } from "../context/authContext";
import alertService from '../services/sweetAlert';
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_END_POINT;

const LyricsGenerator = ({ onPostCreated }) => {
  const [inputText, setInputText] = useState("");
  const [songTitle, setSongTitle] = useState("");
  const [generatedLyrics, setGeneratedLyrics] = useState([]);
  const [fullLyricsText, setFullLyricsText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentLine, setCurrentLine] = useState("");
  const [lineIndex, setLineIndex] = useState(0);
  const [selectedLang, setSelectedLang] = useState("hi");
  const [selectedGenre, setSelectedGenre] = useState("romantic");
  const { currentUser } = useContext(AuthContext);

  // Daily limit tracking
  const [usedToday, setUsedToday] = useState(false);

  // Step tracking: 'input' -> 'lyrics' -> 'saved'
  const [step, setStep] = useState('input');

  // Check if user has already used their daily lyrics
  useEffect(() => {
    const checkLimit = async () => {
      if (!currentUser?.SID) return;

      try {
        const response = await fetch(
          `${API_BASE}/posts/lyricsLimit?userId=${currentUser.SID}`
        );
        const data = await response.json();
        setUsedToday(data.usedToday || false);
      } catch (error) {
        console.error('Error checking lyrics limit:', error);
      }
    };

    checkLimit();
  }, [currentUser]);

  // Supported languages
  const languages = [
    { code: "hi", label: "Hindi" },
    { code: "ta", label: "Tamil" },
    { code: "te", label: "Telugu" },
    { code: "bn", label: "Bengali" },
    { code: "kn", label: "Kannada" },
    { code: "mr", label: "Marathi" },
    { code: "gu", label: "Gujarati" },
    { code: "pa", label: "Punjabi" },
    { code: "pt", label: "Portuguese" },
    { code: "es", label: "Spanish" }
  ];

  // Genres
  const genres = [
    { code: "romantic", label: "Romantic" },
    { code: "sad", label: "Sad" },
    { code: "dance", label: "Dance" },
    { code: "party", label: "Party" },
    { code: "devotional", label: "Devotional" },
    { code: "sufi", label: "Sufi" },
    { code: "ghazal", label: "Ghazal" }
  ];

  // Generate lyrics using Groq
  const fetchLyrics = async () => {
    if (!inputText.trim()) {
      alertService.error("Error", "Please enter a song topic or theme");
      return;
    }

    // Check if user already used their daily lyrics
    if (usedToday) {
      alertService.error("Daily Limit", "You can only generate 1 song per day. Come back tomorrow!");
      return;
    }

    setIsProcessing(true);
    setGeneratedLyrics([]);
    setCurrentLine("");
    setLineIndex(0);

    try {
      const userId = currentUser?.SID || '';
      const response = await fetch(
        `${API_BASE}/posts/generateLyrics?prompt=${encodeURIComponent(inputText)}&lang=${selectedLang}&userId=${userId}`
      );
      const data = await response.json();

      if (response.status === 429) {
        // Daily limit reached
        setUsedToday(true);
        alertService.error("Daily Limit", data.message || "You can only generate 1 song per day.");
        return;
      }

      if (data.generatedLyrics) {
        const lyricsArray = data.generatedLyrics
          .replace(/\\n/g, "\n")
          .replace(/।/g, "\n")
          .split("\n")
          .map(line => line.trim())
          .filter(line => line.length > 0);

        setFullLyricsText(data.generatedLyrics);
        setGeneratedLyrics(lyricsArray);
        setSongTitle(inputText);
        setStep('lyrics');
        startTypingEffect(lyricsArray);
        // Mark as used after successful generation
        setUsedToday(true);
      }
    } catch (error) {
      console.error("API Error:", error);
      alertService.error("Error", "Failed to generate lyrics. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Typing animation effect
  const startTypingEffect = (lyricsArray) => {
    let index = 0;
    const interval = setInterval(() => {
      if (index < lyricsArray.length) {
        setCurrentLine("");
        typeLine(lyricsArray[index], 0);
        setLineIndex(index);
        index++;
      } else {
        clearInterval(interval);
      }
    }, 2000);
  };

  const typeLine = (line, charIndex) => {
    if (charIndex < line.length) {
      setCurrentLine((prev) => prev + line.charAt(charIndex));
      setTimeout(() => typeLine(line, charIndex + 1), 30);
    }
  };

  // Save song and post to feed
  const saveAndPost = async () => {
    if (!currentUser) {
      alertService.error("Login Required", "Please login to post");
      return;
    }

    if (generatedLyrics.length === 0) {
      alertService.error("Error", "No lyrics to post");
      return;
    }

    setIsSaving(true);

    try {
      // Format lyrics as a post with title and genre
      const langLabel = languages.find(l => l.code === selectedLang)?.label || 'Hindi';
      const genreLabel = genres.find(g => g.code === selectedGenre)?.label || 'Romantic';
      const postText = `🎶 ${songTitle || inputText}\n\n${fullLyricsText}\n\n#${genreLabel} #${langLabel} #AILyrics #GeetSuhane`;

      // Post to feed
      const response = await axios.post(`${API_BASE}/posts`, {
        userId: currentUser?.id,
        text: postText
      });

      if (response.data) {
        setStep('saved');
        alertService.success("Posted!", "Your song lyrics have been shared!");
        // Notify parent to refresh posts
        if (onPostCreated) {
          onPostCreated();
        }
      }
    } catch (error) {
      console.error("Post error:", error);
      alertService.error("Error", "Failed to post. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Start over
  const resetForm = () => {
    setInputText("");
    setSongTitle("");
    setGeneratedLyrics([]);
    setFullLyricsText("");
    setStep('input');
    setCurrentLine("");
    setLineIndex(0);
  };

  return (
    <div className="card border-0 shadow-xss rounded-xxl p-4 mb-3">
      <h4 className="fw-700 text-grey-900 font-xss mb-3">
        🎶 AI Lyrics Generator 🎤
      </h4>

      {/* Step 1: Input */}
      {step === 'input' && (
        <>
          {/* Daily limit message */}
          {usedToday && currentUser && (
            <div className="alert alert-warning text-center mb-3 py-2" style={{ fontSize: '13px' }}>
              <span role="img" aria-label="clock">⏰</span> You've used your daily lyrics. Come back tomorrow!
            </div>
          )}

          <div className="row mb-2">
            <div className="col-6">
              <select
                className="form-select form-select-sm"
                value={selectedLang}
                onChange={(e) => setSelectedLang(e.target.value)}
                disabled={usedToday && currentUser}
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>{lang.label}</option>
                ))}
              </select>
            </div>
            <div className="col-6">
              <select
                className="form-select form-select-sm"
                value={selectedGenre}
                onChange={(e) => setSelectedGenre(e.target.value)}
                disabled={usedToday && currentUser}
              >
                {genres.map((genre) => (
                  <option key={genre.code} value={genre.code}>{genre.label}</option>
                ))}
              </select>
            </div>
          </div>

          <ReactTransliterate
            value={inputText}
            onChangeText={(text) => setInputText(text)}
            lang={selectedLang}
            placeholder="Song theme (e.g., 'pyar ka dard', 'khushi')"
            className="form-control mb-2"
            style={{ height: "45px" }}
            disabled={usedToday && currentUser}
          />

          <button
            className="btn btn-primary w-100 rounded-xl"
            style={{ color: '#fff', opacity: (usedToday && currentUser) ? 0.6 : 1 }}
            onClick={fetchLyrics}
            disabled={isProcessing || (usedToday && currentUser)}
          >
            {isProcessing ? "✨ Generating..." : usedToday && currentUser ? "🕐 Come Back Tomorrow" : "🎵 Generate Lyrics"}
          </button>

          {/* Login hint for guests */}
          {!currentUser && (
            <p className="text-center text-muted mt-2 mb-0" style={{ fontSize: '11px' }}>
              Login to save & post your lyrics
            </p>
          )}
        </>
      )}

      {/* Step 2: Show lyrics */}
      {step === 'lyrics' && (
        <>
          <input
            type="text"
            className="form-control fw-600 text-center mb-2"
            placeholder="Song Title"
            value={songTitle}
            onChange={(e) => setSongTitle(e.target.value)}
          />

          <div
            className="bg-light rounded-xl p-3 mb-3"
            style={{ maxHeight: "250px", overflowY: "auto" }}
          >
            {generatedLyrics.map((line, index) => (
              <p
                key={index}
                className="mb-1 font-xsss"
                style={{
                  opacity: index <= lineIndex ? 1 : 0.3,
                  color: index === lineIndex ? '#ff4081' : '#333',
                  transition: 'all 0.3s'
                }}
              >
                {line}
              </p>
            ))}
          </div>

          {currentLine && (
            <p className="text-center fw-600 text-primary mb-2">
              {currentLine}<span className="blink">|</span>
            </p>
          )}

          <div className="d-flex gap-2">
            <button className="btn btn-light flex-grow-1" onClick={resetForm}>
              ↩ Back
            </button>
            <button
              className="btn btn-success flex-grow-1"
              style={{ color: '#fff' }}
              onClick={saveAndPost}
              disabled={isSaving}
            >
              {isSaving ? "Posting..." : "📤 Post"}
            </button>
          </div>
        </>
      )}

      {/* Step 3: Complete */}
      {step === 'saved' && (
        <div className="text-center">
          <div className="mb-3">
            <span style={{ fontSize: "48px" }}>🎉</span>
          </div>
          <h5 className="fw-700 mb-2">Posted!</h5>
          <p className="text-grey-500 font-xsss mb-3">
            Your lyrics are now on your feed
          </p>

          <button className="btn btn-primary w-100 rounded-xl" style={{ color: '#fff' }} onClick={resetForm}>
            ➕ Create Another
          </button>
        </div>
      )}
    </div>
  );
};

export default LyricsGenerator;
